// Kacheln für wenige gewichtige Optionen (Handoff §4, z. B. Verwaltungsart): Icon, Kürzel, Text, Haken.
import type { KeyboardEvent } from 'react';
import { CheckCircle, type Icon } from '@phosphor-icons/react';
import { FeldHuelle } from './FormFeld';
import { FORM, GLANZ, KACHEL } from './form-stil';

export interface FormKachelOption<K extends string> {
  wert: K;
  /** Große Zeile (z. B. „WEG“) */
  kuerzel: string;
  /** Kleine Zeile darunter */
  label: string;
  icon: Icon;
  /** Verlauf der Icon-Kachel, wenn gewählt */
  verlauf: string;
  tip?: string;
}

export interface FormKachelnProps<K extends string> {
  label: string;
  optionen: readonly FormKachelOption<K>[];
  wert: K | '';
  onChange: (wert: K) => void;
  pflicht?: boolean;
  fehler?: boolean;
  tip?: string;
}

export function FormKacheln<K extends string>({ label, optionen, wert, onChange, pflicht, fehler, tip }: FormKachelnProps<K>) {
  const tastatur = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const vor = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const i = optionen.findIndex(o => o.wert === wert);
    const n = optionen.length;
    const naechste = optionen[i < 0 ? 0 : (i + (vor ? 1 : n - 1)) % n];
    if (naechste) onChange(naechste.wert);
  };
  return (
    <FeldHuelle label={label} pflicht={pflicht} fehler={fehler} tip={tip}>
      <div role="radiogroup" aria-label={label} aria-required={pflicht || undefined} aria-invalid={fehler || undefined}
        onKeyDown={tastatur} className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(96px,1fr))', gap: 8, marginTop: 3 }}>
        {optionen.map((o, i) => {
          const an = o.wert === wert;
          const I = o.icon;
          return (
            <button key={o.wert} type="button" role="radio" aria-checked={an} data-tip={o.tip}
              tabIndex={an || (!wert && i === 0) ? 0 : -1}
              onClick={() => onChange(o.wert)}
              className="pf-kachel relative flex cursor-pointer flex-col overflow-hidden border-none text-left"
              style={{
                padding: '11px 12px', borderRadius: 15, gap: 6, font: 'inherit',
                background: an ? KACHEL.aktiv : KACHEL.ruhe,
                boxShadow: an ? KACHEL.aktivRing : fehler ? KACHEL.fehlerRing : KACHEL.ruheRing,
              }}>
              <span aria-hidden className="relative flex items-center justify-center overflow-hidden" style={{
                width: 28, height: 28, borderRadius: 9, background: an ? o.verlauf : KACHEL.iconRuhe,
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.45),0 4px 10px -4px rgba(0,0,0,0.6)',
              }}>
                <span className="absolute inset-0" style={{ background: GLANZ }} />
                <I size={14} weight="fill" color="#fff" className="relative" />
              </span>
              <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em', color: an ? KACHEL.codeAktiv : FORM.text }}>{o.kuerzel}</span>
              <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.25, marginTop: -4, color: 'rgba(255,255,255,0.55)' }}>{o.label}</span>
              {an && <CheckCircle aria-hidden size={15} weight="fill" color={FORM.gold} className="pf-pop absolute" style={{ top: 8, right: 8 }} />}
            </button>
          );
        })}
      </div>
    </FeldHuelle>
  );
}
